/**
 * @module json-schema
 */
/** @pratico/json-schema */
import ajv from 'ajv';


import { EventEmitter, Listener } from './typed-event-emitter';

// import 'core-js/library/fn/object/assign';
import { ValidatorConfig } from './models/validator-config';
import { JsonSchema } from './models/draft-06';
import { ValidationResult } from './models/validation-result';

const defaultConfig: Partial<ValidatorConfig> = {
  allErrors: true,
  coerceTypes: true,
};

/**
 * PraticoJsonSchemaValidator allows to setup ajvObject and run the tests through ajv
 * needs import * as ajv from 'ajv';
 * @export
 * @class PraticoJsonSchemaValidator
 */
export class Validator extends EventEmitter {

  /**
   * Allows to set a listener to the onValidated event
   *
   * @type {{
   *     (handler: { (newValue: ValidationResult): any }): Listener;
   *   }}
   * @memberof Validator
   */
  onValidated: {
    (handler: { (newValue: ValidationResult): any }): Listener;
  } = this.registerEvent<(newValue: ValidationResult) => any>();

  /**
   * Allows to set a listener to the onError event
   *
   * @type {{
   *     (handler: { (newValue: Error): any }): Listener;
   *   }}
   * @memberof Validator
   */
  onError: {
    (handler: { (newValue: Error): any }): Listener;
  } = this.registerEvent<(newValue: Error) => any>();

  /**
   * ajvInstance stores the ajv instance being used to validated
   *
   * @private
   * @type {ajv.Ajv}
   * @memberof Validator
   */
  private ajvInstance: ajv.Ajv;

  private executionId: number = 0;

  /**
   * stores the configuration passed to the validator instance through the {@link constructor}
   *
   * @type {ValidatorConfig}
   * @memberof Validator
   */
  private config: ValidatorConfig;

  /**
   * Creates an instance of PraticoJsonSchemaValidator.
   * @param {ValidatorConfig} config
   *
   * @memberof {@link Validator}
   */
  constructor(config: ValidatorConfig, ajv: { new (options?: ajv.Options): ajv.Ajv }) {
    super();
    this.config = Object.assign(config, defaultConfig);
    this.ajvInstance = new ajv({
      allErrors: config.allErrors,
      coerceTypes: config.coerceTypes,
    });
    this.loadSchemasKeywordsAndFormats(config);
  }

  /**
   * Updates the validator current schema
   * @param {string} id
   * @param {any} schema
   *
   * @memberof Validator
   */
  updateSchema(id: string, schema: any) {
    if (this.ajvInstance.validateSchema(schema)) {
      this.ajvInstance.removeSchema(id);
      this.ajvInstance.addSchema(schema, id);
      schema.$id = id;
      const idx = this.config.schemas.findIndex(schema => schema.$id === id || schema.$ref === id);
      this.config.schemas[idx] = schema;
    } else {
      throw new Error('Could not validate the schema');
    }
  }

  /**
   * Adds a schema to the validator
   *
   * @param {string} id
   * @param {*} schema
   *
   * @memberof Validator
   */
  addSchema(id: string, schema: JsonSchema) {
    schema.$id = id;
    this.config.schemas.push(schema);
    this.ajvInstance.addSchema(schema, id);
  }

  /**
   * Returns a schema, given a $id or $ref
   *
   * @param {string} keyRef
   * @returns {JsonSchema}
   *
   * @memberof Validator
   */
  getSchema(keyRef: string): JsonSchema | undefined {
    return this.config.schemas.find((schema: any) => {
      return schema.$id === keyRef || schema.$ref === keyRef;
    });
  }

  /**
   * Removes a schema from the validator
   *
   * @param {string} id
   *
   * @memberof Validator
   */
  removeSchema(id: string) {
    this.ajvInstance.removeSchema(id);
    const idx = this.config.schemas.findIndex(schema => schema.$id === id || schema.$ref === id);
    if (idx >= 0) {
      this.config.schemas.splice(idx, 1);
    }
  }



  /**
   * Validate some data using some schema loaded into ajv
   *
   * @param {string} schemaId The id which will be used to validate the data
   * @param {*} data The data to be validated
   * @returns {number} the execution id
   *
   * @memberof PraticoJsonSchemaValidator
   */
  validate(schemaId: string | JsonSchema, data: any, callback?: { (res: ValidationResult): void }): number {
    this.executionId += 1;
    const executionId = this.executionId;
    // make this execution assynchronous using setTimeout
    setTimeout(() => {
      let result: boolean | PromiseLike <any>;
      try {
        result = this.ajvInstance.validate(schemaId, data);

        if (typeof result === 'boolean') {
          let resultData;
          if (result) {
            resultData = { executionId: executionId, schemaId: schemaId, data: data, valid: result };
          } else {
            resultData = {
              executionId: executionId,
              schemaId: schemaId,
              data: data,
              valid: result,
              errors: this.ajvInstance.errors,
            };
          }
          this.emit(this.onValidated, resultData);
          if (callback) callback(resultData);
        } else {
          result.then(
            (ok: any) => {
              let resultData = { executionId: executionId, schemaId: schemaId, data: data, valid: ok };
              this.emit(this.onValidated, resultData);
              if (callback) callback(resultData);
            },
            (e: any) => {
              let resultData = {
                executionId: executionId,
                schemaId: schemaId,
                data: data,
                valid: false,
                errors: e,
              };
              this.emit(this.onValidated, resultData);
              if (callback) callback(resultData);
            }
          );
        }
      } catch (e) {
        e.schemaId = schemaId;
        e.data = data;
        e.executionId = executionId;
        this.emit(this.onError, e);
      }
    }, 0);
    return executionId;
  }

  addFormat(name: string, format: ajv.FormatDefinition | RegExp) {
    this.ajvInstance.addFormat(name, format);
  }

  private loadSchemasKeywordsAndFormats(config: ValidatorConfig) {
    if (config && config.schemas !== undefined) {
      config.schemas.forEach(schema => {
        this.ajvInstance.addSchema(schema, schema.$id)
      });
    }
    if (config && config.formats) {
      Object.keys(config.formats).forEach(formatName => {
        if (config.formats !== undefined) {
          this.ajvInstance.addFormat(formatName, config.formats[formatName]);
        }
      });
    }
    if (config && config.keywords) {
      Object.keys(config.keywords).forEach(keywordName => {
        if (config.keywords !== undefined && config.keywords[keywordName] !== undefined) {
          this.ajvInstance.addKeyword(keywordName, config.keywords[keywordName]);
        }
      });
    }
  }
}
